/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useCallback } from 'react';
import { taskNavRef } from '../utils/taskNav';
import {
  ORDER_MAX_INDENT,
  WORKSPACE_KIND_LABELS,
  formatChoiceIds,
  isChoiceAnswerType,
  isOrderAnswerType,
  nextChoiceOptionId,
  normalizeAnswerText,
  parseChoiceIds,
  parseOrderExpectedAlternatives,
} from '../utils/workAttemptHelpers';

const ANSWER_TYPES = [
  { value: 'code', label: 'Код' },
  { value: 'text', label: 'Текст' },
  { value: 'number', label: 'Число' },
  { value: 'file', label: 'Файл' },
  { value: 'single_choice', label: 'Один вариант' },
  { value: 'multiple_choice', label: 'Несколько вариантов' },
  { value: 'match', label: 'Сопоставление' },
  { value: 'order', label: 'Собери программу' },
  { value: 'matrix', label: 'Таблица/матрица' },
  { value: 'weighted_choice', label: 'Ответ баллами' },
];

const WORKSPACE_KINDS = Object.entries(WORKSPACE_KIND_LABELS).map(([value, label]) => ({ value, label }));

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Черновик' },
  { value: 'published', label: 'Опубликовано' },
  { value: 'archived', label: 'Архив' },
];

const LEVEL_OPTIONS = [
  { value: 'базовый', label: 'Базовый' },
  { value: 'стандартный', label: 'Стандартный' },
  { value: 'повышенный', label: 'Повышенный' },
];

function orderLinesFromTask(task) {
  const raw = task?.answer_spec?.items;
  const expected = new Set(
    parseOrderExpectedAlternatives(task?.expected_answer).flat().map(normalizeAnswerText),
  );
  if (!Array.isArray(raw) || raw.length === 0) {
    return [
      { id: 'a', text: '', indent: 0, extra: false, _key: 'a-0' },
      { id: 'b', text: '', indent: 1, extra: false, _key: 'b-1' },
    ];
  }
  return raw.map((o, i) => {
    const id = String(o?.id ?? '').trim() || String.fromCharCode(97 + (i % 26));
    const indentRaw = Number(o?.indent);
    const indent = Number.isFinite(indentRaw) ? Math.max(0, Math.floor(indentRaw)) : 0;
    const extra = expected.size > 0 ? !expected.has(normalizeAnswerText(id)) : o?.indent == null;
    return {
      id,
      text: o.text == null ? '' : String(o.text),
      indent,
      extra,
      _key: `${id}-${i}`,
    };
  });
}

function optionsFromTask(task) {
  const raw = task?.answer_spec?.options;
  if (!Array.isArray(raw) || raw.length === 0) {
    return [
      { id: 'a', text: '', correct: false, _key: 'a-0' },
      { id: 'b', text: '', correct: false, _key: 'b-1' },
    ];
  }
  const correctSet = new Set(parseChoiceIds(task?.expected_answer).map(normalizeAnswerText));
  return raw.map((o, i) => {
    const id = String(o?.id ?? '').trim() || String.fromCharCode(97 + (i % 26));
    return {
      id,
      text: o?.text == null ? '' : String(o.text),
      correct: correctSet.has(normalizeAnswerText(id)),
      _key: `${id}-${i}`,
    };
  });
}

function FieldRow({ label, children, pinned, onTogglePin, onClear, canClear }) {
  return (
    <div className="editor-field">
      <div className="editor-field-head">
        <label>{label}</label>
        <div className="editor-field-actions">
          {onTogglePin && (
            <button
              type="button"
              className="btn-icon"
              onClick={onTogglePin}
              title={pinned ? 'Не закреплять' : 'Закрепить (не сбрасывать при «Сохранить и следующая»)'}
            >
              {pinned ? '🔒' : '🔓'}
            </button>
          )}
          {onClear && canClear && (
            <button type="button" className="btn-icon" onClick={onClear} title="Очистить">
              ✕
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

const CYRILLIC_TO_LATIN = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i',
  й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
  у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '',
  э: 'e', ю: 'yu', я: 'ya',
};

function slugifyFromTypeAndNumber(typeValue, numberValue) {
  const combined = `${typeValue || ''}-${numberValue || ''}`.trim();
  const transliterated = combined
    .toLowerCase()
    .split('')
    .map((char) => CYRILLIC_TO_LATIN[char] ?? char)
    .join('');
  return transliterated
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function TaskEditorForm({
  task,
  initialPinned = {},
  onSave,
  saving,
  error,
}) {
  const [slug, setSlug] = useState(task?.slug ?? '');
  const [title, setTitle] = useState(task?.title ?? '');
  const [statement, setStatement] = useState(task?.statement ?? '');
  const [answerType, setAnswerType] = useState(task?.answer_type ?? 'text');
  const [workspaceKind, setWorkspaceKind] = useState(task?.workspace_kind ?? 'code');
  const [workspaceConfigText, setWorkspaceConfigText] = useState(() => {
    const cfg = task?.workspace_config;
    if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg) || !Object.keys(cfg).length) return '';
    try {
      return JSON.stringify(cfg, null, 2);
    } catch {
      return '';
    }
  });
  const [expectedAnswer, setExpectedAnswer] = useState(task?.expected_answer ?? '');
  const [choiceOptions, setChoiceOptions] = useState(() => optionsFromTask(task));
  const [orderLines, setOrderLines] = useState(() => orderLinesFromTask(task));
  const [status, setStatus] = useState(task?.status ?? 'draft');
  const [sourceAuthor, setSourceAuthor] = useState(task?.source_author ?? '');
  const [sourceName, setSourceName] = useState(task?.source_name ?? '');
  const [sourceNumber, setSourceNumber] = useState(task?.source_number ?? '');
  const [year, setYear] = useState(task?.year ?? '');

  const [solutionCode, setSolutionCode] = useState(task?.solution_code ?? '');
  const [solutionExplanation, setSolutionExplanation] = useState(task?.solution_explanation ?? '');
  const [answerVideo, setAnswerVideo] = useState(() => (task?.answer_video ?? []).join('\n'));

  const [taskTags, setTaskTags] = useState(() =>
    (task?.task_tags ?? []).map((tt) => ({
      tag: tt.tag?.id ?? tt.tag,
      order_in_tag: tt.order_in_tag ?? 0,
      level_in_tag: tt.level_in_tag ?? 'стандартный',
    }))
  );
  const [hints, setHints] = useState(() =>
    (task?.hints ?? []).map((h) => ({ text: h.text ?? '', type: h.type ?? 'text' }))
  );
  const [testCases, setTestCases] = useState(() =>
    (task?.test_cases ?? []).map((tc) => ({
      input_data: tc.input_data ?? '',
      expected_output: tc.expected_output ?? '',
      order: tc.order ?? 0,
      is_hidden: tc.is_hidden ?? false,
    }))
  );

  const [pinned, setPinned] = useState(initialPinned);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [slugEditedManually, setSlugEditedManually] = useState(false);
  const [titleEditedManually, setTitleEditedManually] = useState(false);

  const togglePin = (field) => {
    setPinned((p) => ({ ...p, [field]: !p[field] }));
  };

  const buildPayload = useCallback(() => {
    const isChoice = isChoiceAnswerType(answerType);
    const isOrder = isOrderAnswerType(answerType);
    const filledOptions = choiceOptions
      .map((o) => ({
        id: String(o.id || '').trim(),
        text: String(o.text ?? ''),
        correct: Boolean(o.correct),
      }))
      .filter((o) => o.id || o.text.trim());

    const filledLines = orderLines
      .map((o) => ({
        id: String(o.id || '').trim(),
        text: String(o.text ?? ''),
        indent: Math.max(0, Math.min(ORDER_MAX_INDENT, Number.parseInt(o.indent, 10) || 0)),
        extra: Boolean(o.extra),
      }))
      .filter((o) => o.id || o.text.trim());

    let expected = expectedAnswer.trim();
    let answer_spec = {};
    if (isChoice) {
      const options = filledOptions.map((o, i) => ({
        id: o.id || nextChoiceOptionId(filledOptions.slice(0, i).map((x) => x.id)),
        text: o.text,
      }));
      const correctIds = filledOptions
        .map((o, i) => (o.correct ? (o.id || options[i].id) : null))
        .filter(Boolean);
      expected = answerType === 'multiple_choice'
        ? formatChoiceIds(correctIds, { sort: true })
        : (correctIds[0] || '');
      answer_spec = { options };
    } else if (isOrder) {
      const items = filledLines.map((o, i) => {
        const id = o.id || nextChoiceOptionId(filledLines.slice(0, i).map((x) => x.id));
        const item = { id, text: o.text };
        if (!o.extra) item.indent = o.indent;
        return item;
      });
      expected = items.filter((_, i) => !filledLines[i].extra).map((o) => o.id).join(',');
      answer_spec = { items };
    }

    const payload = {
      slug: slug.trim() || undefined,
      title: title.trim() || undefined,
      statement: statement.trim() || undefined,
      answer_type: answerType,
      workspace_kind: workspaceKind,
      workspace_config: (() => {
        const raw = workspaceConfigText.trim();
        if (!raw) return {};
        try {
          const parsed = JSON.parse(raw);
          return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch {
          return {};
        }
      })(),
      expected_answer: expected,
      answer_spec,
      status,
      source_author: sourceAuthor.trim(),
      source_name: sourceName.trim(),
      source_number: sourceNumber.trim(),
      year: year ? parseInt(year, 10) : null,
      solution_code: solutionCode.trim(),
      solution_explanation: solutionExplanation.trim(),
      answer_video: answerVideo
        .split('\n')
        .map((link) => link.trim())
        .filter(Boolean),
      task_tags: taskTags.filter((tt) => tt.tag).map((tt, i) => ({
        tag: typeof tt.tag === 'object' ? tt.tag.id : tt.tag,
        order_in_tag: tt.order_in_tag ?? i,
        level_in_tag: tt.level_in_tag ?? 'стандартный',
      })),
      hints: hints.filter((h) => h.text.trim()).map((h) => ({ text: h.text.trim(), type: h.type || 'text' })),
      test_cases: testCases.map((tc, i) => ({
        input_data: tc.input_data ?? '',
        expected_output: tc.expected_output ?? '',
        order: tc.order ?? i,
        is_hidden: tc.is_hidden ?? false,
      })),
    };
    return payload;
  }, [
    slug, title, statement, answerType, workspaceKind, workspaceConfigText, expectedAnswer, choiceOptions, orderLines, status,
    sourceAuthor, sourceName, sourceNumber, year,
    solutionCode, solutionExplanation, answerVideo,
    taskTags, hints, testCases,
  ]);

  const resetUnpinned = useCallback(() => {
    if (!pinned.slug) setSlug('');
    if (!pinned.title) setTitle('');
    if (!pinned.statement) setStatement('');
    if (!pinned.answer_type) setAnswerType('text');
    if (!pinned.workspace_kind) setWorkspaceKind('code');
    if (!pinned.workspace_config) setWorkspaceConfigText('');
    if (!pinned.expected_answer) {
      setExpectedAnswer('');
      setChoiceOptions([
        { id: 'a', text: '', correct: false, _key: 'a-0' },
        { id: 'b', text: '', correct: false, _key: 'b-1' },
      ]);
      setOrderLines([
        { id: 'a', text: '', indent: 0, extra: false, _key: 'a-0' },
        { id: 'b', text: '', indent: 1, extra: false, _key: 'b-1' },
      ]);
    }
    if (!pinned.status) setStatus('draft');
    if (!pinned.source_author) setSourceAuthor('');
    if (!pinned.source_name) setSourceName('');
    if (!pinned.source_number) setSourceNumber('');
    if (!pinned.year) setYear('');
    if (!pinned.task_tags) setTaskTags([]);
    if (!pinned.hints) setHints([]);
    if (!pinned.test_cases) setTestCases([]);
    if (!pinned.solution_code) setSolutionCode('');
    if (!pinned.solution_explanation) setSolutionExplanation('');
    if (!pinned.answer_video) setAnswerVideo('');
  }, [pinned]);

  const handleSave = async (andNext) => {
    const payload = buildPayload();
    if (!payload.title) payload.title = 'Без названия';
    await onSave(payload, task ? taskNavRef(task) : undefined);
    if (andNext) resetUnpinned();
  };

  useEffect(() => {
    if (!task) return;
    setSlug(task.slug ?? '');
    setTitle(task.title ?? '');
    setStatement(task.statement ?? '');
    setAnswerType(task.answer_type ?? 'text');
    setWorkspaceKind(task.workspace_kind ?? 'code');
    {
      const cfg = task.workspace_config;
      if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg) || !Object.keys(cfg).length) {
        setWorkspaceConfigText('');
      } else {
        try {
          setWorkspaceConfigText(JSON.stringify(cfg, null, 2));
        } catch {
          setWorkspaceConfigText('');
        }
      }
    }
    setExpectedAnswer(task.expected_answer ?? '');
    setChoiceOptions(optionsFromTask(task));
    setOrderLines(orderLinesFromTask(task));
    setStatus(task.status ?? 'draft');
    setSourceAuthor(task.source_author ?? '');
    setSourceName(task.source_name ?? '');
    setSourceNumber(task.source_number ?? '');
    setYear(task.year ?? '');
    setSolutionCode(task.solution_code ?? '');
    setSolutionExplanation(task.solution_explanation ?? '');
    setAnswerVideo((task.answer_video ?? []).join('\n'));
    setTaskTags(
      (task.task_tags ?? []).map((tt) => ({
        tag: tt.tag?.id ?? tt.tag,
        order_in_tag: tt.order_in_tag ?? 0,
        level_in_tag: tt.level_in_tag ?? 'стандартный',
      }))
    );
    setHints((task.hints ?? []).map((h) => ({ text: h.text ?? '', type: h.type ?? 'text' })));
    setTestCases(
      (task.test_cases ?? []).map((tc) => ({
        input_data: tc.input_data ?? '',
        expected_output: tc.expected_output ?? '',
        order: tc.order ?? 0,
        is_hidden: tc.is_hidden ?? false,
      }))
    );
    setSlugEditedManually(false);
    setTitleEditedManually(false);
  }, [task?.id]);

  useEffect(() => {
    if (task) return;
    setSlugEditedManually(false);
    setTitleEditedManually(false);
  }, [task]);

  useEffect(() => {
    if (task) return;
    const generated = slugifyFromTypeAndNumber(sourceName, sourceNumber);
    if (!slugEditedManually) setSlug(generated);
    if (!titleEditedManually) setTitle(generated);
  }, [task, sourceName, sourceNumber, slugEditedManually, titleEditedManually]);

  const addTaskTag = () => {
    setTaskTags((prev) => [...prev, { tag: null, order_in_tag: prev.length, level_in_tag: 'стандартный' }]);
  };
  const updateTaskTag = (index, field, value) => {
    setTaskTags((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const removeTaskTag = (index) => setTaskTags((prev) => prev.filter((_, i) => i !== index));

  const addHint = () => setHints((prev) => [...prev, { text: '', type: 'text' }]);
  const updateHint = (index, field, value) => {
    setHints((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const removeHint = (index) => setHints((prev) => prev.filter((_, i) => i !== index));

  const addTestCase = () =>
    setTestCases((prev) => [...prev, { input_data: '', expected_output: '', order: prev.length, is_hidden: false }]);
  const updateTestCase = (index, field, value) => {
    setTestCases((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const removeTestCase = (index) => setTestCases((prev) => prev.filter((_, i) => i !== index));

  const isChoice = isChoiceAnswerType(answerType);
  const isOrder = isOrderAnswerType(answerType);

  const addChoiceOption = () => {
    setChoiceOptions((prev) => {
      const id = nextChoiceOptionId(prev.map((o) => o.id));
      return [...prev, { id, text: '', correct: false, _key: `${id}-${prev.length}-${Date.now()}` }];
    });
  };
  const updateChoiceOption = (index, field, value) => {
    setChoiceOptions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const setChoiceCorrect = (index, checked) => {
    setChoiceOptions((prev) => {
      if (answerType === 'single_choice') {
        return prev.map((o, i) => ({ ...o, correct: i === index ? checked : false }));
      }
      const next = [...prev];
      next[index] = { ...next[index], correct: checked };
      return next;
    });
  };
  const removeChoiceOption = (index) => {
    setChoiceOptions((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== index)));
  };

  const addOrderLine = () => {
    setOrderLines((prev) => {
      const id = nextChoiceOptionId(prev.map((o) => o.id));
      return [...prev, { id, text: '', indent: 0, extra: false, _key: `${id}-${prev.length}-${Date.now()}` }];
    });
  };
  const updateOrderLine = (index, field, value) => {
    setOrderLines((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const removeOrderLine = (index) => {
    setOrderLines((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== index)));
  };

  return (
    <form
      className="task-editor-form"
      onSubmit={(e) => { e.preventDefault(); handleSave(false); }}
    >
      {error && <div className="alert alert-error">{error}</div>}

      <section className="editor-section">
        <h3>Основное</h3>
        <FieldRow label="Название сборника" pinned={pinned.source_name} onTogglePin={() => togglePin('source_name')} onClear={() => setSourceName('')} canClear={!!sourceName}>
          <input type="text" value={sourceName} onChange={(e) => setSourceName(e.target.value)} placeholder="Например, Сайт Полякова, ЕГЭ 2024" className="form-input" />
        </FieldRow>
        <FieldRow label="Номер в сборнике" pinned={pinned.source_number} onTogglePin={() => togglePin('source_number')} onClear={() => setSourceNumber('')} canClear={!!sourceNumber}>
          <input type="text" value={sourceNumber} onChange={(e) => setSourceNumber(e.target.value)} placeholder="Например, 27" className="form-input" />
        </FieldRow>
        <FieldRow label="Автор задачи" pinned={pinned.source_author} onTogglePin={() => togglePin('source_author')} onClear={() => setSourceAuthor('')} canClear={!!sourceAuthor}>
          <input type="text" value={sourceAuthor} onChange={(e) => setSourceAuthor(e.target.value)} placeholder="Автор условия (не сборник)" className="form-input" />
        </FieldRow>
        <FieldRow label="Slug" pinned={pinned.slug} onTogglePin={() => togglePin('slug')} onClear={() => setSlug('')} canClear={!!slug}>
          <input
            type="text"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugEditedManually(true);
            }}
            placeholder="Например, ege-27"
            className="form-input"
          />
        </FieldRow>
        <FieldRow label="Название" pinned={pinned.title} onTogglePin={() => togglePin('title')} onClear={() => setTitle('')} canClear={!!title}>
          <input
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleEditedManually(true);
            }}
            placeholder="Название задачи"
            className="form-input"
          />
        </FieldRow>
      </section>

      <section className="editor-section">
        <h3>Условие</h3>
        <FieldRow label="Текст задания" pinned={pinned.statement} onTogglePin={() => togglePin('statement')}>
          <textarea value={statement} onChange={(e) => setStatement(e.target.value)} rows={8} className="form-input" placeholder="Условие (Markdown)" />
        </FieldRow>
      </section>

      <section className="editor-section">
        <h3>Ответ</h3>
        <FieldRow label="Тип ответа" pinned={pinned.answer_type} onTogglePin={() => togglePin('answer_type')}>
          <select value={answerType} onChange={(e) => setAnswerType(e.target.value)} className="form-select">
            {ANSWER_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Рабочее место" pinned={pinned.workspace_kind} onTogglePin={() => togglePin('workspace_kind')}>
          <select value={workspaceKind} onChange={(e) => setWorkspaceKind(e.target.value)} className="form-select">
            {WORKSPACE_KINDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Конфиг workspace (JSON)" pinned={pinned.workspace_config} onTogglePin={() => togglePin('workspace_config')}>
          <textarea
            value={workspaceConfigText}
            onChange={(e) => setWorkspaceConfigText(e.target.value)}
            rows={5}
            className="form-input font-mono"
            placeholder={'Опционально. Игры: {"width":520,"height":480}. Робот: {"rows":8,"cols":8,"start":[0,0],"walls":[[0,1,"W"]]}. Черепаха: {"grid_step":20}'}
          />
        </FieldRow>
        {isChoice ? (
          <div className="editor-choice-options">
            <div className="editor-choice-options-head">Варианты ответа</div>
            {choiceOptions.map((opt, i) => (
              <div key={opt._key || opt.id || i} className="editor-choice-row">
                <input
                  type="text"
                  className="form-input editor-choice-id"
                  value={opt.id}
                  onChange={(e) => updateChoiceOption(i, 'id', e.target.value)}
                  placeholder="id"
                  aria-label={`Id варианта ${i + 1}`}
                />
                <input
                  type="text"
                  className="form-input editor-choice-text"
                  value={opt.text}
                  onChange={(e) => updateChoiceOption(i, 'text', e.target.value)}
                  placeholder="Текст варианта"
                  aria-label={`Текст варианта ${i + 1}`}
                />
                <label className="editor-choice-correct">
                  <input
                    type={answerType === 'multiple_choice' ? 'checkbox' : 'radio'}
                    name={answerType === 'single_choice' ? 'editor-choice-correct' : undefined}
                    checked={Boolean(opt.correct)}
                    onChange={(e) => setChoiceCorrect(i, e.target.checked)}
                  />
                  Верный
                </label>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => removeChoiceOption(i)}
                  disabled={choiceOptions.length <= 2}
                >
                  Удалить
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-outline btn-sm" onClick={addChoiceOption}>
              + Вариант
            </button>
          </div>
        ) : isOrder ? (
          <div className="editor-choice-options">
            <div className="editor-choice-options-head">
              Нужные строки — в правильном порядке. У лишних поставьте галочку.
            </div>
            {orderLines.map((line, i) => (
              <div key={line._key || line.id || i} className="editor-choice-row editor-order-row">
                <input
                  type="text"
                  className="form-input editor-choice-id"
                  value={line.id}
                  onChange={(e) => updateOrderLine(i, 'id', e.target.value)}
                  placeholder="id"
                  aria-label={`Id строки ${i + 1}`}
                />
                <input
                  type="text"
                  className="form-input editor-choice-text font-mono"
                  value={line.text}
                  onChange={(e) => updateOrderLine(i, 'text', e.target.value)}
                  placeholder="строка кода"
                  aria-label={`Текст строки ${i + 1}`}
                />
                <label className="editor-choice-correct">
                  Отступ
                  <input
                    type="number"
                    className="form-input editor-order-indent"
                    min={0}
                    max={ORDER_MAX_INDENT}
                    disabled={Boolean(line.extra)}
                    value={line.indent}
                    onChange={(e) => updateOrderLine(i, 'indent', parseInt(e.target.value, 10) || 0)}
                    aria-label={`Отступ строки ${i + 1}`}
                  />
                </label>
                <label className="editor-choice-correct">
                  <input
                    type="checkbox"
                    checked={Boolean(line.extra)}
                    onChange={(e) => updateOrderLine(i, 'extra', e.target.checked)}
                  />
                  Лишняя
                </label>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => removeOrderLine(i)}
                  disabled={orderLines.length <= 2}
                >
                  Удалить
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-outline btn-sm" onClick={addOrderLine}>
              + Строка
            </button>
          </div>
        ) : (
          <FieldRow label="Правильный ответ / JSON" pinned={pinned.expected_answer} onTogglePin={() => togglePin('expected_answer')}>
            <textarea value={expectedAnswer} onChange={(e) => setExpectedAnswer(e.target.value)} rows={3} className="form-input font-mono" placeholder="Число, текст или JSON" />
          </FieldRow>
        )}
      </section>

      <section className="editor-section">
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => setShowAdvanced((prev) => !prev)}
        >
          {showAdvanced ? 'Скрыть дополнительные поля' : 'Показать дополнительные поля'}
        </button>
      </section>

      {showAdvanced && (
        <>
          <section className="editor-section">
            <h3>Дополнительно</h3>
            <FieldRow label="Показывать" pinned={pinned.status} onTogglePin={() => togglePin('status')}>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-select">
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FieldRow>
            <FieldRow label="Год" pinned={pinned.year} onTogglePin={() => togglePin('year')} onClear={() => setYear('')} canClear={!!year}>
              <input type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="2024" className="form-input" style={{ width: '6rem' }} />
            </FieldRow>
          </section>

          <section className="editor-section">
            <h3>Теги</h3>
            {taskTags.map((tt, i) => (
              <div key={i} className="editor-tag-row">
                <input type="number" placeholder="ID тега" value={tt.tag ?? ''} onChange={(e) => updateTaskTag(i, 'tag', e.target.value ? parseInt(e.target.value, 10) : null)} className="form-input" style={{ width: '5rem' }} />
                <select value={tt.level_in_tag} onChange={(e) => updateTaskTag(i, 'level_in_tag', e.target.value)} className="form-select">
                  {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input type="number" placeholder="Порядок" value={tt.order_in_tag} onChange={(e) => updateTaskTag(i, 'order_in_tag', parseInt(e.target.value, 10) || 0)} className="form-input" style={{ width: '4rem' }} />
                <button type="button" className="btn btn-outline btn-sm" onClick={() => removeTaskTag(i)}>Удалить</button>
              </div>
            ))}
            <button type="button" className="btn btn-outline btn-sm" onClick={addTaskTag}>+ Тег</button>
          </section>

          <section className="editor-section">
            <h3>Тесты (для код)</h3>
            {testCases.map((tc, i) => (
              <div key={i} className="editor-test-row">
                <textarea placeholder="Ввод" value={tc.input_data} onChange={(e) => updateTestCase(i, 'input_data', e.target.value)} rows={2} className="form-input font-mono" />
                <textarea placeholder="Вывод" value={tc.expected_output} onChange={(e) => updateTestCase(i, 'expected_output', e.target.value)} rows={2} className="form-input font-mono" />
                <label><input type="checkbox" checked={tc.is_hidden} onChange={(e) => updateTestCase(i, 'is_hidden', e.target.checked)} /> Скрытый</label>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => removeTestCase(i)}>Удалить</button>
              </div>
            ))}
            <button type="button" className="btn btn-outline btn-sm" onClick={addTestCase}>+ Тест</button>
          </section>

          <section className="editor-section">
            <h3>Подсказки</h3>
            {hints.map((h, i) => (
              <div key={i} className="editor-hint-row">
                <select value={h.type} onChange={(e) => updateHint(i, 'type', e.target.value)} className="form-select">
                  <option value="text">Текст</option><option value="code">Код</option><option value="image">Изображение</option><option value="link">Ссылка</option>
                </select>
                <textarea value={h.text} onChange={(e) => updateHint(i, 'text', e.target.value)} rows={2} className="form-input" placeholder="Подсказка" />
                <button type="button" className="btn btn-outline btn-sm" onClick={() => removeHint(i)}>Удалить</button>
              </div>
            ))}
            <button type="button" className="btn btn-outline btn-sm" onClick={addHint}>+ Подсказка</button>
          </section>

          <section className="editor-section">
            <h3>Решение</h3>
            <FieldRow label="Разбор" pinned={pinned.solution_explanation} onTogglePin={() => togglePin('solution_explanation')}>
              <textarea value={solutionExplanation} onChange={(e) => setSolutionExplanation(e.target.value)} rows={4} className="form-input" />
            </FieldRow>
            <FieldRow label="Эталонный код" pinned={pinned.solution_code} onTogglePin={() => togglePin('solution_code')}>
              <textarea value={solutionCode} onChange={(e) => setSolutionCode(e.target.value)} rows={6} className="form-input font-mono" />
            </FieldRow>
            <FieldRow label="Ссылки на видеоразбор (по одной в строке)" pinned={pinned.answer_video} onTogglePin={() => togglePin('answer_video')}>
              <textarea
                value={answerVideo}
                onChange={(e) => setAnswerVideo(e.target.value)}
                rows={4}
                className="form-input"
                placeholder={'https://...'}
              />
            </FieldRow>
          </section>
        </>
      )}

      <div className="editor-form-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</button>
        {!task && (
          <button type="button" className="btn btn-outline" onClick={() => handleSave(true)} disabled={saving}>
            Сохранить и следующая
          </button>
        )}
      </div>
    </form>
  );
}
